import { createSelector } from 'reselect';
import filterConfig from '../../config/filter';
import getQueryParamOverride from '@gameday/utils/getQueryParamOverride';

const useContentApiOverride = getQueryParamOverride('useContentApi');

const overrideMapping = {
    highlights: 'video'
};

const generateInningsStructure = (plays = [], allPlays) => {
    const inningsTemp = {};

    plays.forEach((play) => {
        const data = allPlays[play];
        const halfInning = data?.about?.halfInning;
        const inning = data?.about?.inning;

        if (inning && halfInning) {
            if (typeof inningsTemp[inning] === 'undefined') {
                inningsTemp[inning] = {
                    top: [],
                    bottom: [],
                    inning
                };
            };
            inningsTemp[inning][halfInning].push(play);
        }
    });

    return Object.values(inningsTemp);
};

const getDefault = (playsByInning) => {
    const temp = [];
    (playsByInning || []).forEach((data, index) => {
        const { top = [], bottom = [] } = data;
        temp.push({
            inning: index + 1,
            top,
            bottom
        });
    });
    return temp;
};

const getParallaxAdPlayIndex = (playList, adIndex) => {
    if (adIndex && adIndex <= playList.length) {
        return playList[adIndex];
    } else {
        return -1;
    }
};

const getVideos = (allPlays, playVideoMap) => {
    const inningsTemp = {};

    allPlays.forEach((play, index) => {
        const { resultPlayGuid } = play;

        if (resultPlayGuid && playVideoMap?.[resultPlayGuid]) {
            const halfInning = play?.about?.halfInning;
            const inning = play?.about?.inning;

            if (inning && halfInning) {
                if (typeof inningsTemp[inning] === 'undefined') {
                    inningsTemp[inning] = {
                        top: [],
                        bottom: [],
                        inning
                    };
                };
                inningsTemp[inning][halfInning].push(index);
            }
        }
    });
    return Object.values(inningsTemp);
};

const getPlayVideoMap = state => {
    const content = ((useContentApiOverride ?? state.config?.useContentApi) ? state.content : state.contentGraph) || {};
    return content?.playVideoMap;
};
const getConfig = state => state.config;
const getLoading = state => state.loading?.isLoading;
const getGamePk = state => state?.gumbo?.gamePk;
const getDetail = state => state?.views?.detail;
const getSummary = state => state.summary || {};
const getFilterIndex = (state) => getSummary(state)?.summaryFilterIndex ?? 0;
const getAllPlays = (state) => getSummary(state)?.allPlays;
const getPlaysByInning = (state) => getSummary(state)?.playsByInning;
const getScoringPlays = (state) => getSummary(state)?.scoringPlays;
const getHomeRuns = (state) => getSummary(state)?.homeRuns;
const getStrikeouts = (state) => getSummary(state)?.strikeouts;
const getLeadChange = (state) => getSummary(state)?.leadChange;
const getWinProb = (state) => getSummary(state)?.winProb;
const getKeyMoments = (state) => getSummary(state)?.keyMoments;
const getHardHits = (state) => getSummary(state)?.hardHits;
const getEnableSummaryFiltersLive = state =>  getConfig(state)?.enableSummaryFiltersLive || {};
const getEnableSummaryFiltersFinal = state =>  getConfig(state)?.enableSummaryFiltersFinal || {};
const getSummaryParallaxAdSlotIndex = state =>  getConfig(state)?.summaryParallaxAdSlotIndex || {};

const getHasHighlights = state => {
    return (useContentApiOverride ?? state.config?.useContentApi) ?
        state?.content?.data?.videos?.length > 0 :
        state?.contentGraph?.data?.videoContent?.length > 0
};
const getTab = state => state?.views?.game_tab;

const getInfo = (data) => {
    const { allPlays, playsByInning, scoringPlays, hardHits, homeRuns, strikeouts, leadChange, winProb, keyMoments, enableSummaryFiltersLive, enableSummaryFiltersFinal, summaryParallaxAdSlotIndex, playVideoMap, detail, hasHighlights, tab, isFinal } = data;
    const enabledFilters = isFinal ? enableSummaryFiltersFinal : enableSummaryFiltersLive;
    const summaryFilters = [];
    let filterIndex = 0;
    let { summaryFilterIndex = 0 } = data;

   
    Object.keys(enabledFilters).forEach(filter => {
        if (enabledFilters[filter]) {
            const filterData = data?.[filter];
           
            const hideEmptyFilter = filterConfig[filter]?.hideIfEmpty && (typeof filterData === 'undefined' || (Array.isArray(filterData) && filterData?.length === 0));
            if (!hideEmptyFilter) {
                summaryFilters.push(filterConfig[filter]);
                filterIndex++;
                if (tab !== 'video' && (overrideMapping[filter] === tab || filter === detail)) {
                    summaryFilterIndex = filterIndex - 1;
                }
            }
        }
    });

    const activeFilter = summaryFilters[summaryFilterIndex]?.type;
    const activeFilters = {};
    activeFilters[activeFilter] = true;
    if (!activeFilter) {
        activeFilters.all = true;
    }

    let listByType = [];
    let tempParallaxAdIndex = -1;
    let showVideoButton = false
    switch (activeFilter) {
    case 'highlights':
        listByType = getVideos(allPlays, playVideoMap);
        showVideoButton = hasHighlights;
        break;
    case 'scoringPlays':
        listByType = generateInningsStructure(scoringPlays, allPlays);
        break;
    case 'homeRuns':
        listByType = generateInningsStructure(homeRuns, allPlays);
        break;
    case 'hardHits':
        listByType = generateInningsStructure(hardHits, allPlays);
        break;
    case 'strikeouts':
        listByType = generateInningsStructure(strikeouts, allPlays);
        break;
    case 'leadChange':
        listByType = generateInningsStructure(leadChange, allPlays);
        break;
    case 'winProb':
        listByType = generateInningsStructure(winProb, allPlays);
        break;
    case 'keyMoments':
        listByType = generateInningsStructure(keyMoments, allPlays);
        tempParallaxAdIndex = getParallaxAdPlayIndex(keyMoments, summaryParallaxAdSlotIndex);
        break;
    default:
        tempParallaxAdIndex = allPlays.length > summaryParallaxAdSlotIndex ? summaryParallaxAdSlotIndex : -1;
        listByType = getDefault(playsByInning);
    }

    // We should only show Parallax Ads on the first (default) tab
    const showParallaxAd = activeFilter === Object.keys(enabledFilters)[0];
    // Also check to see if the index exists in the game so far
    const parallaxAdIndex = showParallaxAd && tempParallaxAdIndex;
    const filterKey = summaryFilters ? summaryFilters?.reduce((accumulator, currentValue) => accumulator + currentValue?.type,'',) : ''
    const selectedFilterIndex = [summaryFilterIndex];

    return {
        summaryFilters,
        activeFilters,
        list: listByType,
        parallaxAdIndex,
        detail,
        summaryFilterIndex: selectedFilterIndex,
        showVideoButton,
        filterKey
    };
};

export const getSummaryState = (isFinal) => {

    return createSelector(
        [getFilterIndex, getAllPlays, getPlaysByInning, getScoringPlays, getHardHits, getHomeRuns, getStrikeouts, getLeadChange, getWinProb, getKeyMoments,  getEnableSummaryFiltersLive, getEnableSummaryFiltersFinal, getSummaryParallaxAdSlotIndex, getPlayVideoMap, getLoading, getGamePk, getDetail, getHasHighlights, getTab],
        (summaryFilterIndex, allPlays, playsByInning, scoringPlays, hardHits, homeRuns, strikeouts, leadChange, winProb, keyMoments, enableSummaryFiltersLive, enableSummaryFiltersFinal, summaryParallaxAdSlotIndex, playVideoMap, loading, gamePk, detail, hasHighlights, tab) => {
            return {
                ...getInfo({ hardHits, summaryFilterIndex, allPlays, playsByInning, scoringPlays, homeRuns, strikeouts, leadChange, winProb, keyMoments, enableSummaryFiltersLive, enableSummaryFiltersFinal, summaryParallaxAdSlotIndex, playVideoMap, detail, hasHighlights, tab, isFinal }),
                gamePk,
                isLoading: loading,
            }
        }
    );
};
